import { requireChartOfflineEligibility } from './chartEligibility';
import { enqueueRequest, getAllRequests, removeRequest } from "./offlineQueue";
import {
  shouldExclude,
  requiresConfirmedWrite,
  isFormDataRequest,
  isApiEndpoint,
  isClinicalChartWrite,
  isClinicalRecoveryWrite,
  shouldAutoReplayQueuedRequest,
  shouldDiscardQueuedRequest,
} from "./config";
import { saveChartDraft } from './chartDraftStore';
import appConfig from "../config";
import { getUserInfoFromToken } from "../utils/api/common";
import { createReplayContext, isBillingSession } from './replayContext';
import { isPendingIntakeUnavailableHttpResponse } from '../utils/pendingIntakeAvailability';
import * as offlineSession from './offlineSession';

const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
// This marker only prevents the fetch proxy from queuing a replay again. The
// clinical replay header has server-side eligibility semantics and must not be
// added to ordinary queued requests (including global-admin operations).
const QUEUED_REPLAY = Symbol('queuedReplay');

// Browsers cancel in-flight requests when a page is refreshed or navigated
// away from. That is not an offline failure and must not create a durable
// queue entry (or clinical draft) that appears on the next page load.
export function isAbortedRequest(error) {
  return error?.name === 'AbortError';
}

// navigator.onLine is only a connectivity hint. Safari and mobile browsers can
// continue reporting "online" while the API is unreachable, and a service
// worker or gateway may resolve fetch with a transient HTTP response instead
// of rejecting it. Those responses must enter device-draft recovery too.
export function shouldPersistClinicalResponseOnDevice(response) {
  if (!response) return true;
  return response.status === 0
    || response.status === 408
    || response.status === 425
    || response.status === 429
    || response.status >= 500;
}

// Retain the old export name for callers and tests while the underlying queue
// is migrated away from legacy terminology.
export const shouldAutoReplayLegacyRequest = shouldAutoReplayQueuedRequest;

function jsonResponse(data) {
  return new Response(JSON.stringify(data), {
    status: 200,
    headers: {
      "Content-Type": "application/json",
      "X-Vela-Local-Subscription-Bypass": "true",
    },
  });
}

function localNoContentResponse() {
  return new Response(null, {
    status: 204,
    headers: {
      "X-Vela-Local-Subscription-Bypass": "true",
    },
  });
}

async function isSubscriptionRequiredResponse(res) {
  try {
    const body = await res.clone().json();
    return body?.status === "None" || body?.error === "Subscription required";
  } catch {
    return false;
  }
}

function getLocalSubscriptionBypassResponse(url, method) {
  if (method !== "GET") return null;

  const { pathname } = new URL(url, window.location.origin);
  if (/^\/api\/revenue-cycle(?:\/|$)/.test(pathname)) return null;
  const userInfo = getUserInfoFromToken() || {};
  const organizationReferenceId =
    userInfo.organizationReferenceId ||
    userInfo.organizationId ||
    "local-organization";

  if (pathname === "/api/billing/subscription") {
    return jsonResponse({
      tier: "Free",
      status: "None",
      currentPeriodEnd: null,
      canceledAt: null,
      trialEnd: null,
    });
  }

  if (pathname === "/api/organization/my") {
    return jsonResponse({
      id: organizationReferenceId,
      referenceId: organizationReferenceId,
      name: "Local Practice",
      accountMode: "Live",
      sandbox: {
        isSandbox: false,
        isReadOnly: false,
        daysRemaining: null,
      },
    });
  }

  if (/^\/api\/user\/[^/]+$/.test(pathname)) {
    return jsonResponse({
      id: userInfo.referenceId || userInfo.sub,
      referenceId: userInfo.referenceId || userInfo.sub,
      email: userInfo.email || "",
      firstName: userInfo.firstName || "",
      lastName: userInfo.lastName || "",
      role: userInfo.role || localStorage.getItem("userRole") || "midwife",
      organizationId: userInfo.organizationId || organizationReferenceId,
      organizationReferenceId,
      organizationRole: userInfo.organizationRole || "Admin",
      __localSubscriptionBypass: true,
    });
  }

  if (pathname === "/api/admin/system-notification") {
    return localNoContentResponse();
  }

  if (pathname === "/api/fax/inbox/unread-count") {
    return jsonResponse({ count: 0, unreadCount: 0 });
  }

  if (/^\/api\/midwives\/[^/]+\/clients$/.test(pathname)) {
    return jsonResponse([]);
  }

  if (/^\/api\/midwives\/[^/]+\/appointments$/.test(pathname)) {
    return jsonResponse([]);
  }

  if (/^\/api\/organizations\/[^/]+\/(conversations|messages|members|channels\/browse)$/.test(pathname)) {
    return jsonResponse([]);
  }

  // Keep local development navigable when the API applies subscription checks
  // broadly. Specific endpoints above preserve object-shaped responses where
  // callers require them; remaining gated reads are safest as empty lists.
  if (pathname.startsWith("/api/")) {
    return jsonResponse([]);
  }

  return null;
}

const INITIAL_RETRY_DELAY_MS = 2000;
const MAX_RETRY_DELAY_MS = 30000;

let drainPromise = null;
let retryTimer = null;
let retryDelayMs = INITIAL_RETRY_DELAY_MS;

function isTransientReplayResponse(response) {
  return response.status === 408
    || response.status === 425
    || response.status === 429
    || response.status >= 500;
}

async function drainQueueOnce({ force = false } = {}) {
  if (isBillingSession()) return { retryable: false };
  if (!force && !navigator.onLine) return { retryable: false };
  const replay = createReplayContext();
  try {
    let items;
    try {
      items = await getAllRequests();
    } catch (error) {
      console.error('Could not inspect the legacy offline queue:', error);
      return { retryable: true };
    }
    if (!Array.isArray(items)) return { retryable: false };
    let retryable = false;
    const failedStatusCodes = new Set();
    for (const item of items) {
      if (!replay.canContinue()) return { retryable: false };
      if (shouldDiscardQueuedRequest(item)) {
        await removeRequest(item.id);
        window.dispatchEvent(new CustomEvent('vela:legacy-queue-changed', {
          detail: { requestId: item.id, status: 'discarded' },
        }));
        continue;
      }
      // Old clinical writes lack the new idempotency and conflict protections.
      // Preserve them for support-assisted recovery instead of replaying them.
      if (!shouldAutoReplayQueuedRequest(item)) continue;

      const headers = new Headers(item.headers || {});
      const token = replay.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      try {
        const res = await fetch(item.url, {
          method: item.method,
          headers,
          body: item.body,
          signal: replay.signal,
          [QUEUED_REPLAY]: true,
        });
        if (!replay.canContinue()) return { retryable: false };
        if (res.status === 401) {
          // Stop draining; wait for re-login
          try {
            window.dispatchEvent(new CustomEvent("vela:queue-auth-required"));
          } catch {}
          return { retryable: false };
        }
        if (!res.ok) {
          if (await isPendingIntakeUnavailableHttpResponse(res)) {
            await removeRequest(item.id);
            window.dispatchEvent(new CustomEvent('vela:legacy-queue-changed', {
              detail: { requestId: item.id, status: 'discarded' },
            }));
            continue;
          }
          // Permanent 4xx responses need user attention, not a hot retry loop.
          // Transient gateway/rate-limit responses participate in backoff.
          failedStatusCodes.add(res.status);
          retryable = retryable || isTransientReplayResponse(res);
          continue;
        }
        await removeRequest(item.id);
        window.dispatchEvent(new CustomEvent('vela:legacy-queue-changed', {
          detail: { requestId: item.id, status: 'replayed' },
        }));
      } catch {
        if (!replay.canContinue()) return { retryable: false };
        // A short API or radio interruption does not always fire the browser's
        // `online` event, especially in an installed mobile PWA. Keep retrying
        // with backoff so the queue can recover without a page refresh.
        retryable = true;
        break;
      }
    }
    return { retryable, failedStatusCodes: [...failedStatusCodes] };
  } finally {
    replay.dispose();
  }
}

async function drainQueue(options) {
  if (drainPromise) {
    if (!options?.force) return drainPromise;
    // A manual probe should not be swallowed by a startup drain that skipped
    // because navigator.onLine was false. Let that pass finish, then force the
    // real connectivity check requested by the user.
    await drainPromise;
  }
  drainPromise = drainQueueOnce(options).finally(() => {
    drainPromise = null;
  });
  return drainPromise;
}

function scheduleQueueRetry(delay = retryDelayMs) {
  if (retryTimer || !navigator.onLine) return;
  retryTimer = window.setTimeout(async () => {
    retryTimer = null;
    const { retryable } = await drainQueue();
    if (retryable) {
      retryDelayMs = Math.min(MAX_RETRY_DELAY_MS, retryDelayMs * 2);
      scheduleQueueRetry();
    } else {
      retryDelayMs = INITIAL_RETRY_DELAY_MS;
    }
  }, delay);
}

function triggerQueueDrain({ resetBackoff = false } = {}) {
  if (resetBackoff) {
    retryDelayMs = INITIAL_RETRY_DELAY_MS;
    if (retryTimer) {
      window.clearTimeout(retryTimer);
      retryTimer = null;
    }
  }
  drainQueue().then(({ retryable }) => {
    if (retryable) scheduleQueueRetry();
  });
}

function shouldShowSubscriptionPaywall() {
  const role = (localStorage.getItem("userRole") || "").toLowerCase();
  return role !== "client" && role !== "client_user" && !role.includes("client");
}

async function check402(res, url, method) {
  if (res.status === 402 && appConfig.isSubscriptionGatingEnabled && shouldShowSubscriptionPaywall()) {
    res.clone().json().then((body) => {
      try {
        window.dispatchEvent(
          new CustomEvent("vela:subscription-required", {
            detail: { status: body.status || "None" },
          })
        );
      } catch {}
    }).catch(() => {
      try {
        window.dispatchEvent(
          new CustomEvent("vela:subscription-required", {
            detail: { status: "None" },
          })
        );
      } catch {}
    });
  }

  if (res.status === 402 && !appConfig.isSubscriptionGatingEnabled) {
    const fallback = getLocalSubscriptionBypassResponse(url, method);
    if (fallback && await isSubscriptionRequiredResponse(res)) {
      return fallback;
    }
  }

  return res;
}

export function installFetchProxy() {
  if (window.__velaFetchProxyInstalled) return;
  window.__velaFetchProxyInstalled = true;

  const originalFetch = window.fetch.bind(window);
  const observedFetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input.url;
    try {
      const response = await originalFetch(input, init);
      if (isApiEndpoint(url)) window.dispatchEvent(new CustomEvent('vela:api-reachability', {
        detail: { reachable: ![408, 502, 503, 504].includes(response.status) },
      }));
      return response;
    } catch (error) {
      if (isApiEndpoint(url) && !isAbortedRequest(error)) window.dispatchEvent(new CustomEvent('vela:api-reachability', { detail: { reachable: false } }));
      throw error;
    }
  };
  window.fetch = async (input, init = {}) => {
    const { [QUEUED_REPLAY]: queuedReplay, ...networkInit } = init;
    init = networkInit;
    const url = typeof input === "string" ? input : input.url;
    const method = (init.method || input?.method || "GET").toUpperCase();
    if (isApiEndpoint(url)) {
      const headers = new Headers(init.headers || input?.headers || {});
      headers.set('X-Sena-App-Build', process.env.REACT_APP_BUILD_SHA || 'offline-containment-v1');
      init = { ...init, headers };
    }

    if (WRITE_METHODS.has(method) && isApiEndpoint(url) && init.body === undefined && typeof Request !== 'undefined' && input instanceof Request) {
      init = { ...init, body: await input.clone().text() };
    }

    // The billing workspace never persists requests or clinical drafts locally.
    if (isBillingSession()) return check402(await observedFetch(input, init), url, method);

    // Only handle write methods for API endpoints
    if (WRITE_METHODS.has(method) && isApiEndpoint(url)) {
      const urlObj = new URL(url, window.location.origin);
      const isReplay = queuedReplay || new Headers(init.headers || {}).get('X-Sena-Offline-Replay') === 'true';

      // Queue replays must reach the real network exactly once. Passing a
      // failed replay back through the queuing branch would create a new queue
      // record and show another warning on every retry.
      if (isReplay) {
        return check402(await observedFetch(input, init), url, method);
      }

      if (requiresConfirmedWrite(urlObj.pathname, method)) {
        try {
          return check402(await observedFetch(input, init), url, method);
        } catch (error) {
          if (isAbortedRequest(error)) throw error;
          throw new Error('Save could not be confirmed. Keep this form open, reconnect, and check the saved record before retrying.');
        }
      }

      // Clinical chart writes use a durable, inspectable draft workflow. A
      // failed write is deliberately returned as an error so chart forms do
      // not mistake local persistence for server success.
      if (isClinicalChartWrite(urlObj.pathname, method) || isClinicalRecoveryWrite(urlObj.pathname, method)) {
        const recoveryOnly = isClinicalRecoveryWrite(urlObj.pathname, method);
        try {
          if (navigator.onLine) {
            const networkResponse = await observedFetch(input, init);
            const continueLocal = networkResponse.status === 401
              && offlineSession.canContinueOfflineCharting?.();
            if (!shouldPersistClinicalResponseOnDevice(networkResponse) && !continueLocal) {
              return check402(networkResponse, url, method);
            }
          }
        } catch (error) {
          if (isAbortedRequest(error)) throw error;
        }
        const match = urlObj.pathname.match(/(prenatal|postpartum|newborn|lactation|well-woman|custom)-visits/);
        const clientPathMatch = window.location.pathname.match(/^\/charting\/([^/]+)/);
        const pregnancyPathMatch = urlObj.pathname.match(/^\/api\/pregnancies\/([^/]+)\//);
        const chartPathMatch = urlObj.pathname.match(/-visits\/([^/]+)$/);
        await requireChartOfflineEligibility(clientPathMatch ? decodeURIComponent(clientPathMatch[1]) : null);
        let draft;
        try {
          draft = await saveChartDraft({
            url, method, headers: init.headers, body: init.body,
            chartType: match?.[1] || (recoveryOnly ? 'labor-chart-recovery' : 'clinical-chart'),
            recoveryOnly,
            coalesce: !recoveryOnly,
            clientReferenceId: clientPathMatch ? decodeURIComponent(clientPathMatch[1]) : null,
            pregnancyReferenceId: pregnancyPathMatch?.[1] || null,
            chartId: chartPathMatch?.[1] || null,
            // A chart that has never been edited has no UpdatedAt value. The
            // API uses CreatedAt as its effective version in that case, so the
            // device draft must do the same.
            baseVersion: getClinicalBaseVersion(init.headers, init.body),
          });
        } catch (error) {
          console.error('Offline chart could not be saved on this device:', error);
          const message = 'Not saved on device. Keep this chart open and reconnect before leaving.';
          window.dispatchEvent(new CustomEvent('vela:chart-device-save-failed', { detail: { message } }));
          return new Response(JSON.stringify({ error: message, deviceSaveFailed: true }), {
            status: 507,
            headers: { 'Content-Type': 'application/json', 'X-Sena-Device-Save-Failed': 'true' },
          });
        }
        window.dispatchEvent(new CustomEvent('vela:chart-saved-on-device', { detail: { draftId: draft.draftId } }));
        return new Response(JSON.stringify({
          error: 'Saved on this device, but not yet synced to the server.',
          draftId: draft.draftId,
          status: 'saved-on-device',
        }), { status: 503, headers: { 'Content-Type': 'application/json', 'X-Sena-Draft-Id': draft.draftId } });
      }
      
      // Check if endpoint should be excluded from queuing
      if (shouldExclude(urlObj.pathname, method)) {
        // Excluded endpoint - pass through to network
        return check402(await observedFetch(input, init), url, method);
      }
      
      // Don't queue FormData requests (file uploads)
      // FormData can't be easily serialized and stored in IndexedDB
      if (isFormDataRequest(init)) {
        return check402(await observedFetch(input, init), url, method);
      }
      
      // If offline, enqueue and return synthetic response
      if (!navigator.onLine) {
        await enqueueRequest({
          url,
          method,
          headers: init.headers,
          body: init.body,
          createdAt: Date.now(),
        });
        try {
          window.dispatchEvent(new CustomEvent("vela:write-queued"));
        } catch {}
        scheduleQueueRetry();
        const queuedResponse = new Response(JSON.stringify({ queued: true }), {
          status: 202,
          headers: { "Content-Type": "application/json" },
        });
        return queuedResponse;
      }
      
      // If online, try network first; on network error, enqueue
      try {
        const res = await observedFetch(input, init);
        return check402(res, url, method);
      } catch (error) {
        if (isAbortedRequest(error)) throw error;
        // Network error - queue for later
        await enqueueRequest({
          url,
          method,
          headers: init.headers,
          body: init.body,
          createdAt: Date.now(),
        });
        try {
          window.dispatchEvent(new CustomEvent("vela:write-queued"));
        } catch {}
        scheduleQueueRetry();
        return new Response(JSON.stringify({ queued: true }), {
          status: 202,
          headers: { "Content-Type": "application/json" },
        });
      }
    }

    if (method === 'GET' && isApiEndpoint(url) && new Headers(init.headers).get('X-Sena-Offline-Prepare') !== 'true') {
      const prepared = () => offlineSession.readPreparedChartResponse?.(input, init);
      if (!navigator.onLine || offlineSession.shouldReadPreparedDirectly?.()) {
        const cached = await prepared();
        if (cached) return cached;
      }
      try {
        const response = await observedFetch(input, init);
        if (shouldPersistClinicalResponseOnDevice(response)) {
          const cached = await prepared();
          if (cached) return cached;
        }
        return check402(response, url, method);
      } catch (error) {
        if (isAbortedRequest(error)) throw error;
        const cached = await prepared();
        if (cached) return cached;
        throw error;
      }
    }
    return check402(await observedFetch(input, init), url, method);
  };

  // Drain on startup and on every useful connectivity hint. Installed mobile
  // PWAs do not reliably emit `online` for short radio/API interruptions, so
  // focus, visibility, and the backoff timer all provide recovery paths.
  window.addEventListener("online", () => triggerQueueDrain({ resetBackoff: true }));
  window.addEventListener("focus", () => triggerQueueDrain({ resetBackoff: true }));
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) triggerQueueDrain({ resetBackoff: true });
  });
  triggerQueueDrain();
}

// Expose manual drain for later use (e.g., after login)
export async function replayQueuedRequests() {
  retryDelayMs = INITIAL_RETRY_DELAY_MS;
  if (retryTimer) {
    window.clearTimeout(retryTimer);
    retryTimer = null;
  }
  // A manual retry deliberately probes the API even when navigator.onLine is
  // stale. That browser hint is the failure mode this PWA escape hatch exists
  // to handle.
  const result = await drainQueue({ force: true });
  if (result.retryable) scheduleQueueRetry();
  return result;
}
export function getClinicalBaseVersion(headers, body) {
  const headerVersion = new Headers(headers || {}).get('X-Sena-Base-Version');
  if (headerVersion) return headerVersion;

  try {
    const metadata = JSON.parse(body || '{}');
    const dto = metadata?.dto || metadata?.Dto || {};
    return metadata?.updatedAt || metadata?.UpdatedAt
      || metadata?.createdAt || metadata?.CreatedAt
      || dto?.updatedAt || dto?.UpdatedAt
      || dto?.createdAt || dto?.CreatedAt
      || null;
  } catch {
    return null;
  }
}
